import {
  Sidebar,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
} from '@backstage/core-components';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import type { ExtensionDefinition } from '@backstage/frontend-plugin-api';
import { Shortcuts } from '@backstage-community/plugin-shortcuts';
import { FeatureFlagged } from '@backstage/core-app-api';
import { RiLink, RiSearchLine, RiMenuLine, RiTeamFill } from '@remixicon/react';
import {
  BackstageLogoIcon,
  SettingsButton,
} from '@internal/backstage-plugin-hm-react';
import { SidebarLogo } from './sidebar-logo';
import { SidebarControls } from './sidebar-controls';
import styles from './sidebar.module.css';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';

const SidebarDivider = ({ label }: { label: string }) => {
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={styles.separator} data-open={isOpen}>
      {label}
    </div>
  );
};

export const SidebarContent: ExtensionDefinition = NavContentBlueprint.make({
  params: {
    component: ({ navItems }) => {
      const nav = navItems.withComponent(item => {
        // Needed to not highlight catalog and My Team at the same time
        // Todoo: How to check the route for the "catalog page" in a better way - as it can be overridden
        const isEnd = item.href?.includes('catalog');

        return (
          <SidebarItem
            icon={() => item.icon}
            to={item.href}
            text={item.title}
            end={isEnd}
          />
        );
      });

      return (
        <Sidebar>
          <SidebarGroup label="Home" icon={<BackstageLogoIcon />} to="/">
            <SidebarLogo />
          </SidebarGroup>

          <SidebarGroup label="Search" icon={<RiSearchLine />} to="/search">
            {nav.take('page:search')}
          </SidebarGroup>

          <SidebarGroup label="Plugins" icon={<RiMenuLine />}>
            <SidebarDivider label="workspace" />
            <MyGroupsSidebarItem
              singularTitle="My Team"
              pluralTitle="My Teams"
              filter={{ 'spec.type': 'team' }}
              icon={RiTeamFill as any}
            />
            {nav.take('page:catalog')}
            {nav.take('page:techdocs')}
            {nav.take('page:dashboards-landing-page')}
            {nav.take('page:pulse')}
            {nav.take('page:link-library')}
            {nav.take('page:explore-landing-page')}
            <SidebarDivider label="templates" />
            {nav.take('page:scaffolder')}
          </SidebarGroup>

          <FeatureFlagged with="use-feature-discovery-for-sidebar">
            <SidebarScrollWrapper>
              {nav.rest({ sortBy: 'title' })}
            </SidebarScrollWrapper>
          </FeatureFlagged>

          <SidebarSpace />

          <SidebarGroup label="Shortcuts" icon={<RiLink />}>
            <Shortcuts allowExternalLinks />
          </SidebarGroup>

          <SidebarGroup label="Controls" icon={<SettingsButton />}>
            <SidebarControls />
          </SidebarGroup>
        </Sidebar>
      );
    },
  },
});
